/**
 * Engine Atlas axis label — left/top grid-margin labels that teach the 3×3
 * geography explicitly per scope §3 + Atlas Day 2 visual-layout commitment D.
 *
 * Two orientations:
 *   - horizontal: column label (rendered above grid cells); altitude axis values
 *     "input" / "calculation" / "decision"
 *   - vertical: row label (rendered to the left of grid cells); altitude axis values
 *     "position" / "transformation" / "substrate"
 *
 * "Vertical" here means row-label (left-margin row identification), not text-
 * orientation rotation. Labels remain horizontal-text for readability across
 * vertical and horizontal axis labels uniformly.
 *
 * Style per Atlas Day 2 visual-layout commitment D: subdued text-ink-muted,
 * small font, lowercase. No color coding by altitude/stage; no per-row/column
 * border styling. Axis labels carry geography teaching without UI-convention
 * overhead.
 */

import type { JSX } from 'react';
import clsx from 'clsx';

/** @public — pinned-by-source-assertion (U11 census C1): a root test regex-asserts this export's source text. */
export type AxisOrientation = 'horizontal' | 'vertical';

export interface AxisLabelProps {
  axis: AxisOrientation;
  label: string;
}

export default function AxisLabel({ axis, label }: AxisLabelProps): JSX.Element {
  return (
    <div
      data-axis-label={axis}
      className={clsx(
        'text-xs lowercase text-ink-muted select-none',
        axis === 'horizontal' ? 'text-center pb-1' : 'text-right pr-3 self-center'
      )}
    >
      {label}
    </div>
  );
}
